import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import type { LabStep } from "../services/api";
import { apiService, type LabDetailResponse, type LabProgressResponse } from "../services/api";
import { LabAnimation } from "../components/LabAnimations";
import { Avatar } from "../components/Avatar";
import { TUICodeEditor } from "../components/TUICodeEditor";
import { CodeDisplay } from "../components/CodeDisplay";
import { TypingTest, type TypingTestResults } from "../components/TypingTest";
import { AsciiConnectDots } from "../components/AsciiConnectDots";
import { useAuth } from "../hooks/useAuth";
import { avatarForLevel } from "../constants/avatars";
import { trackClientEvent } from "../utils/analytics";
import { validateCodeChallenge } from "../utils/codeValidation";
import { containsHtml, htmlContent } from "../utils/renderHtml";
import "../css/Duo.css";

export const Route = createFileRoute("/labs/$id")({
  component: DuoLesson,
});

// XP configuration based on lab difficulty
const DIFFICULTY_XP = {
  beginner: { full: 5, partialFloor: 2 },
  intermediate: { full: 10, partialFloor: 5 },
  advanced: { full: 15, partialFloor: 8 },
} as const;

type DifficultyKey = keyof typeof DIFFICULTY_XP;

const normalizeOutput = (output: string | undefined | null) => {
  if (!output) return "";
  return output
    .replace(/\r\n/g, "\n")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
};

function DuoLesson() {
  const { id: labId } = Route.useParams();
  const { user } = useAuth();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [taskProgress, setTaskProgress] = useState<Record<string, Set<number>>>({});
  const [codeInputs, setCodeInputs] = useState<Record<string, string>>({});
  const [showSolution, setShowSolution] = useState<Record<string, boolean>>({});
  const [isEmbedded, setIsEmbedded] = useState(false);
  
  // API data state
  const [labData, setLabData] = useState<LabDetailResponse | null>(null);
  const [labProgress, setLabProgress] = useState<LabProgressResponse | null>(null);
  const [savingProgress, setSavingProgress] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track course and page information for redirect
  const [courseInfo, setCourseInfo] = useState<{courseId: string, pageIndex: number} | null>(null);

  // Lesson state
  const [hearts, setHearts] = useState(3);
  const [xp, setXp] = useState(0);
  const [phase, setPhase] = useState<'answer' | 'feedback'>('answer');
  const [result, setResult] = useState<null | 'correct' | 'partial' | 'incorrect'>(null);
  const [lastAwardType, setLastAwardType] = useState<'full' | 'partial' | 'loss' | null>(null);
  const [lastXpDelta, setLastXpDelta] = useState<number | null>(null);
  const [xpAnimationType, setXpAnimationType] = useState<'success' | 'partial' | 'loss' | null>(null);
  const xpAnimationTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedChoice, setSelectedChoice] = useState<number | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationState, setValidationState] = useState<'valid' | 'invalid' | null>(null);
  const [recheckValidation, setRecheckValidation] = useState<'correct' | 'incorrect' | null>(null);
  
  // New step type states
  const [multiSelectAnswers, setMultiSelectAnswers] = useState<Record<string, Set<number>>>({});
  const [fillInBlankAnswers, setFillInBlankAnswers] = useState<Record<string, string[]>>({});
  const [codeOutputAnswers, setCodeOutputAnswers] = useState<Record<string, string>>({});
  const [typingTestResults, setTypingTestResults] = useState<Record<string, {
    completed: boolean;
    wpm: number;
    accuracy: number;
    mistakes: number;
    alreadyAwarded: boolean;
  }>>({});
  const [typingTestSessions, setTypingTestSessions] = useState<Record<string, number>>({});
  const [asciiConnectDotsAnswers, setAsciiConnectDotsAnswers] = useState<Record<string, string[]>>({});
  const [rapidMatchStates, setRapidMatchStates] = useState<Record<string, {
    selectedKey: string | null;
    selectedValue: string | null;
    phase: 'flash' | 'answer' | 'feedback';
    keyOrder: string[];
    valueOrder: string[];
    isFlashing: boolean;
  }>>({});
  const rapidMatchTimers = useRef<Record<string, {
    shuffle?: ReturnType<typeof setInterval>;
    flash?: ReturnType<typeof setTimeout>;
  }>>({});
  

  // Track step results for batch submission at completion
  const [stepResults, setStepResults] = useState<Array<{
    stepIndex: number;
    outcome: 'full' | 'partial' | 'incorrect';
    partialCredit?: number;
  }>>([]);

  const [userLevel, setUserLevel] = useState(1);
  const activeAvatarId = avatarForLevel(userLevel);

  // Refs for dynamic mascot targeting
  const choiceRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const ctaRef = useRef<HTMLButtonElement | null>(null);
  const [mascotPos, setMascotPos] = useState<{ top: number; left: number } | null>(null);

  const setChoiceRef = useCallback((index: number) => (el: HTMLDivElement | null) => {
    choiceRefs.current[index] = el;
  }, []);

  const shuffleArray = useCallback((items: string[]) => {
    const array = [...items];
    for (let index = array.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(Math.random() * (index + 1));
      const currentValue = array[index];
      array[index] = array[swapIndex];
      array[swapIndex] = currentValue;
    }
    return array;
  }, []);

  const startRapidMatchFlash = useCallback((step: LabStep) => {
    if (step.type !== 'rapid_match') return;

    const stepId = step.id;
    const durationMs = 2500;
    const keys = step.keys || [];
    const values = step.values || [];

    const shuffledKeys = shuffleArray(keys);
    const shuffledValues = shuffleArray(values);

    setRapidMatchStates((prev) => ({
      ...prev,
      [stepId]: {
        selectedKey: null,
        selectedValue: null,
        phase: 'flash',
        keyOrder: shuffledKeys,
        valueOrder: shuffledValues,
        isFlashing: true
      }
    }));

    const existingTimers = rapidMatchTimers.current[stepId];
    if (existingTimers?.shuffle) {
      clearInterval(existingTimers.shuffle);
    }
    if (existingTimers?.flash) {
      clearTimeout(existingTimers.flash);
    }

    const shuffleTimer = setInterval(() => {
      setRapidMatchStates((prev) => {
        const current = prev[stepId];
        if (!current || current.phase !== 'flash') return prev;
        return {
          ...prev,
          [stepId]: {
            ...current,
            keyOrder: shuffleArray(keys),
            valueOrder: shuffleArray(values)
          }
        };
      });
    }, 150);

    const flashTimer = setTimeout(() => {
      if (rapidMatchTimers.current[stepId]?.shuffle) {
        clearInterval(rapidMatchTimers.current[stepId].shuffle);
      }
      setRapidMatchStates((prev) => {
        const current = prev[stepId];
        if (!current) return prev;
        return {
          ...prev,
          [stepId]: {
            ...current,
            phase: 'answer',
            isFlashing: false
          }
        };
      });
    }, durationMs);

    rapidMatchTimers.current[stepId] = {
      shuffle: shuffleTimer,
      flash: flashTimer
    };
  }, [shuffleArray]);

  const clearStepState = (stepId?: string) => {
    if (!stepId) return;

    setMultiSelectAnswers((prev) => {
      if (!prev[stepId]) return prev;
      const next = { ...prev };
      delete next[stepId];
      return next;
    });

    setFillInBlankAnswers((prev) => {
      if (!prev[stepId]) return prev;
      const next = { ...prev };
      delete next[stepId];
      return next;
    });

    setCodeOutputAnswers((prev) => {
      if (!prev[stepId]) return prev;
      const next = { ...prev };
      delete next[stepId];
      return next;
    });

    setCodeInputs((prev) => {
      if (!prev[stepId]) return prev;
      const next = { ...prev };
      delete next[stepId];
      return next;
    });

    setTypingTestResults((prev) => {
      if (!prev[stepId]) return prev;
      const next = { ...prev };
      delete next[stepId];
      return next;
    });

    setShowSolution((prev) => {
      if (!prev[stepId]) return prev;
      const next = { ...prev };
      delete next[stepId];
      return next;
    });

    setRapidMatchStates((prev) => {
      if (!prev[stepId]) return prev;
      const next = { ...prev };
      delete next[stepId];
      return next;
    });

    const rapidMatchTimer = rapidMatchTimers.current[stepId];
    if (rapidMatchTimer?.shuffle) {
      clearInterval(rapidMatchTimer.shuffle);
    }
    if (rapidMatchTimer?.flash) {
      clearTimeout(rapidMatchTimer.flash);
    }
    delete rapidMatchTimers.current[stepId];
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    setIsEmbedded(params.get("embedded") === "1");
    
    // Extract course and page information for redirect
    const courseId = params.get("courseId");
    const pageIndex = params.get("pageIndex");
    if (courseId && pageIndex) {
      setCourseInfo({
        courseId,
        pageIndex: parseInt(pageIndex, 10)
      });
    }

    return () => {
      if (xpAnimationTimeout.current) {
        clearTimeout(xpAnimationTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      Object.values(rapidMatchTimers.current).forEach((timer) => {
        if (timer?.shuffle) {
          clearInterval(timer.shuffle);
        }
        if (timer?.flash) {
          clearTimeout(timer.flash);
        }
      });
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiService.getGamificationStats()
      .then((stats) => {
        if (!cancelled) {
          const lvl = stats?.user?.level;
          if (typeof lvl === 'number' && Number.isFinite(lvl)) {
            setUserLevel(lvl);
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Load lab data from API
  useEffect(() => {
    let isMounted = true;

    const loadLab = async () => {
      try {
        setLoading(true);
        setSaveError(null);
        const labDetail = await apiService.getLabById(labId);
        let progressData: LabProgressResponse | null = null;

        try {
          progressData = await apiService.getLabProgress(labId);
        } catch (progressError) {
          const status = (progressError as Error & { status?: number }).status;
          if (status && status !== 404) {
            throw progressError;
          }
        }

        if (!isMounted) return;

        setLabData(labDetail);
        if (progressData) {
          let latestProgress = progressData;
          const labNumericId = Number.parseInt(labDetail.lab.id, 10);
          const hadInProgressState = !progressData.completed && (
            (progressData.currentStep ?? 0) > 0 ||
            (progressData.progress ?? 0) > 0 ||
            (progressData.pointsEarned ?? 0) > 0
          );

          if (!Number.isNaN(labNumericId) && hadInProgressState) {
            try {
              latestProgress = await apiService.updateLabProgress({
                labId: labNumericId,
                progress: 0,
                currentStep: 0,
                completed: false,
              });
            } catch (resetError) {
              console.error('Failed to reset lab progress on reopen:', resetError);
            }
          }

          setLabProgress(latestProgress);
          const totalSteps = labDetail.lab.steps?.length || 0;
          if (totalSteps > 0) {
            setCurrentStepIndex(0);
          }

          if (latestProgress.completed && typeof latestProgress.pointsEarned === 'number') {
            setXp(latestProgress.pointsEarned);
          } else {
            setXp(0);
          }
        } else {
          setLabProgress(null);
          setCurrentStepIndex(0);
          setXp(0);
        }
      } catch (err) {
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load lab');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadLab();

    return () => {
      isMounted = false;
    };
  }, [labId]);
  
  // Compute mascot position near target element
  useEffect(() => {
    let target: HTMLElement | null = null;
    if (phase === 'answer') {
      if (selectedChoice != null) target = choiceRefs.current[selectedChoice] as HTMLElement | null;
    } else {
      // feedback phase: point toward CTA for continue
      target = ctaRef.current;
    }
    if (target) {
      const rect = target.getBoundingClientRect();
      // Position the mascot above and to the right of the target
      const top = Math.max(16, rect.top + window.scrollY - 120);
      const left = Math.min(window.innerWidth - 160, rect.left + window.scrollX + rect.width + 16);
      setMascotPos({ top, left });
    } else {
      setMascotPos(null);
    }
  }, [phase, result, selectedChoice]);

  const lab = labData?.lab;

  const persistProgress = useCallback(
    async (stepsCompleted: number, completed: boolean) => {
      // Skip progress updates for completed lab retries
      const isRetry = typeof window !== "undefined" && 
        new URLSearchParams(window.location.search).get("retry") === "1";
      if (isRetry && labProgress?.completed) {
        return;
      }
      if (!lab) return;

      const totalSteps = lab.steps?.length || 1;
      const clampedCompleted = Math.min(Math.max(stepsCompleted, 0), totalSteps);
      const progressPercent = totalSteps === 0 ? 100 : Math.round((clampedCompleted / totalSteps) * 100);
      const currentStepValue = totalSteps === 0 ? 0 : Math.min(clampedCompleted, totalSteps - 1);

      try {
        setSavingProgress(true);
        setSaveError(null);
        const payload = {
          labId: Number(lab.id),
          progress: progressPercent,
          currentStep: currentStepValue,
          ...(completed ? { completed: true } : {}),
        };

        const updated = await apiService.updateLabProgress(payload);
        setLabProgress(updated);
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Failed to save progress');
      } finally {
        setSavingProgress(false);
      }
    },
    [lab, labProgress]
  );

  const triggerXpAnimation = useCallback((award: 'full' | 'partial' | 'loss') => {
    if (xpAnimationTimeout.current) {
      clearTimeout(xpAnimationTimeout.current);
    }
    const animationType = award === 'full' ? 'success' : award === 'partial' ? 'partial' : 'loss';
    setXpAnimationType(animationType);
    xpAnimationTimeout.current = setTimeout(() => {
      setXpAnimationType(null);
    }, 1200);
  }, []);

  const courseTitle = labProgress?.lab.course?.title || ""; // We'll need to get this from course data if needed

  const totalSteps = lab?.steps?.length || 1;
  const currentStep = lab?.steps?.[Math.min(currentStepIndex, Math.max(totalSteps - 1, 0))];
  const progressPercent = labProgress
    ? labProgress.progress
    : Math.round(((currentStepIndex + 1) / totalSteps) * 100);
  const displayedStepIndex = Math.min(currentStepIndex + 1, totalSteps);

  useEffect(() => {
    if (currentStep?.type === 'rapid_match') {
      startRapidMatchFlash(currentStep);
    }
  }, [currentStep?.id, currentStep?.type, startRapidMatchFlash]);

  const feedbackMessage = (() => {
    if (phase !== 'feedback' || !result) {
      return null;
    }
    const xpText = lastXpDelta !== null && lastXpDelta !== 0
      ? `${lastXpDelta > 0 ? '+' : ''}${lastXpDelta} XP`
      : null;
    if (result === 'correct') {
      return xpText ? `${xpText}! Great job!` : 'Nice! Great job!';
    }
    if (result === 'partial') {
      return xpText ? `${xpText}. Partial credit awarded!` : 'Almost there. Partial credit!';
    }
    if (currentStep?.type === 'code' && currentStep.solution) {
      return xpText ? `${xpText}. Craft the perfect syntax and logic!` : 'Craft the perfect syntax and logic!';
    }
    return xpText ?? "Close!";
  })();




  // Helper to determine avatar mood based on state
  const getAvatarMood = () => {
    if (phase === 'feedback') {
      if (result === 'correct') return 'happy';
      if (result === 'partial') return 'neutral';
      if (result === 'incorrect') {
        if (currentStep?.type === 'code' && currentStep.solution) {
          return 'concerned';
        }
        return 'sad';
      }
    }
    if (phase === 'answer' && (currentStep?.type === 'quiz' || currentStep?.type === 'code')) return 'thinking';
    return 'neutral';
  };

  const avatarMood = getAvatarMood();

  useEffect(() => {
    trackClientEvent('avatar_shown', {
      avatarId: activeAvatarId,
      mood: avatarMood,
      labId,
      phase,
      result,
    });
  }, [activeAvatarId, avatarMood, labId, phase, result]);

  if (loading) {
    return (
      <div className="duo-root" style={isEmbedded ? { minHeight: "100%", background: "transparent" } : undefined}>
        <div className="duo-stage">
          <div className="duo-card">
            <div className="duo-card-title">Loading Lab...</div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error || !lab) {
    return (
      <div className="duo-root">
        <div className="duo-stage">
          <div className="duo-card">
            <div className="duo-card-title">Error</div>
            <div className="duo-card-body">
              <div className="duo-bubble">
                <div className="duo-bubble-text">{error || 'Lab not found'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if lab is premium and user doesn't have premium subscription
  const isPremiumLab = lab.isPremium;
  const isLocked = isPremiumLab && user?.subscription !== 'premium';
  const isMobileLayout = typeof window !== "undefined" && window.innerWidth <= 640;

  if (isLocked) {
    return (
      <div className="duo-root">
      <div
        className="duo-hud"
        style={{
          padding: isMobileLayout ? "12px 16px" : undefined,
          rowGap: isMobileLayout ? 12 : undefined,
        }}
      >

          <div className="duo-hud-left"><div className="duo-lives"><span className="heart on">❤</span><span className="heart on">❤</span><span className="heart on">❤</span></div></div>
          <div className="duo-hud-center"><div className="duo-progress"><div className="duo-progress-bar"><div className="duo-progress-fill" style={{ width: '0%' }} /></div><div className="duo-progress-meta">Locked</div></div></div>
        </div>
        <div className="duo-stage">
          <div className="duo-avatar"><Avatar avatarId={activeAvatarId} size={100} mood="neutral" /></div>
          <div className="duo-card">
            <div className="duo-card-title">🔒 Premium Lab</div>
            <div className="duo-card-meta">This lab requires premium access</div>
            <div className="duo-card-body">
              <div className="duo-bubble">
                <div className="duo-bubble-text">You need a premium subscription to access this lab.</div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginTop: 16 }}>
                <Link to="/pricing" className="duo-btn success">Upgrade to Premium →</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!lab.steps || lab.steps.length === 0) {
    return (
      <div className="duo-root">
        <div className="duo-stage">
          <div className="duo-avatar"><Avatar avatarId={activeAvatarId} size={100} mood="neutral" /></div>
          <div className="duo-card">
            <div className="duo-card-title">No Lab Steps Available</div>
            <div className="duo-card-meta">This lab hasn't been configured yet.</div>
            <div className="duo-card-body">
              <div className="duo-bubble">
                <div className="duo-bubble-text">
                  This lab doesn’t have any steps right now. Please check back later or pick another lab to keep learning.
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                <Link to="/labs" className="duo-btn subtle">Back to Labs →</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }


  // Duolingo-like helpers
  const canCheck = (step: LabStep) => {
    switch (step.type) {
      case 'quiz':
        return selectedChoice !== null;
      case 'task': {
        const prog = taskProgress[step.id] || new Set<number>();
        return prog.size > 0; // allow checking when user attempted
      }
      case 'code':
        return (codeInputs[step.id] || '').trim().length > 0;
      case 'multi_select':
        return (multiSelectAnswers[step.id]?.size || 0) > 0;
      case 'fill_in_blank': {
        const answers = fillInBlankAnswers[step.id] || [];
        return answers.some(ans => ans.trim().length > 0);
      }
      case 'code_output':
        return (codeOutputAnswers[step.id] || '').trim().length > 0;
      case 'typing-test':
        return typingTestResults[step.id]?.completed || false;
      case 'ascii_connect_dots':
        return (asciiConnectDotsAnswers[step.id]?.length || 0) >= 2;
      case 'rapid_match': {
        const matchState = rapidMatchStates[step.id];
        return Boolean(matchState?.selectedKey && matchState?.selectedValue);
      }
      case 'read':
      default:
        return true;
    }
  };

  const evaluate = (step: LabStep): 'correct' | 'partial' | 'incorrect' => {
    switch (step.type) {
      case 'quiz':
        return selectedChoice === step.answerIndex ? 'correct' : 'incorrect';
      // DISABLED: task step-type
      // case 'task': {
      //   const prog = taskProgress[step.id] || new Set<number>();
      //   const total = step.checklist?.length || 0;
      //   if (total === 0) {
      //     return prog.size > 0 ? 'correct' : 'incorrect';
      //   }
      //   if (prog.size === 0) return 'incorrect';
      //   if (prog.size === total) return 'correct';
      //   return 'partial';
      // }
      case 'code': {
        const validationResult = validateCodeChallenge(codeInputs[step.id] || '', step);
        setValidationErrors(validationResult.errors);
        return validationResult.isValid ? 'correct' : 'incorrect';
      }
      case 'multi_select': {
        const selected = multiSelectAnswers[step.id] || new Set<number>();
        const correct = new Set(step.correctIndices || []);
        
        // Calculate correctness
        let correctSelections = 0;
        let incorrectSelections = 0;
        
        selected.forEach(idx => {
          if (correct.has(idx)) correctSelections++;
          else incorrectSelections++;
        });
        
        // Full credit: all correct, none incorrect
        if (correctSelections === correct.size && incorrectSelections === 0) {
          return 'correct';
        }
        
        // Partial credit if allowed and some correct
        if (step.allowPartial && correctSelections > 0 && correctSelections > incorrectSelections) {
          return 'partial';
        }
        
        return 'incorrect';
      }
      case 'fill_in_blank': {
        const answers = fillInBlankAnswers[step.id] || [];
        const blanks = step.blanks || [];
        
        let correctCount = 0;
        blanks.forEach((blank, idx) => {
          if (validateBlank(answers[idx] || '', blank)) {
            correctCount++;
          }
        });
        
        if (correctCount === blanks.length) return 'correct';
        if (correctCount > 0) return 'partial';
        return 'incorrect';
      }
      case 'code_output': {
        const userOutput = normalizeOutput(codeOutputAnswers[step.id] || '');
        const acceptable = step.acceptableOutputs || [];
        
        // Check against all acceptable outputs (case-insensitive with whitespace normalization)
        const allValid = [step.correctOutput, ...acceptable];
        const isCorrect = allValid.some(output => normalizeOutput(output) === userOutput);
        
        return isCorrect ? 'correct' : 'incorrect';
      }
      case 'typing-test': {
        // Typing test always returns 'correct' if completed
        // XP is only awarded on first completion
        return 'correct';
      }
      case 'ascii_connect_dots': {
        const userSequence = asciiConnectDotsAnswers[step.id] || [];
        const correctSequence = step.correctSequence || [];
        
        // Exact match required
        if (JSON.stringify(userSequence) === JSON.stringify(correctSequence)) {
          return 'correct';
        }
        
        // Partial credit logic (if enabled)
        if (step.allowPartial) {
          let correctCount = 0;
          const minLength = Math.min(userSequence.length, correctSequence.length);
          
          for (let i = 0; i < minLength; i++) {
            if (userSequence[i] === correctSequence[i]) {
              correctCount++;
            } else {
              break; // Stop at first mismatch
            }
          }
          
          const partialPercent = correctCount / correctSequence.length;
          if (partialPercent >= 0.5) {
            return 'partial';
          }
        }
        
        return 'incorrect';
      }
      case 'rapid_match': {
        const matchState = rapidMatchStates[step.id];
        const selectedKey = matchState?.selectedKey;
        const selectedValue = matchState?.selectedValue;
        const keyCorrect = selectedKey === step.correctKey;
        const valueCorrect = selectedValue === step.correctValue;

        if (keyCorrect && valueCorrect) {
          return 'correct';
        }

        if (step.allowPartial && (keyCorrect || valueCorrect)) {
          return 'partial';
        }

        return 'incorrect';
      }
      case 'read':
      default:
        return 'correct';
    }
  };

  const handleCheck = async () => {
    if (!currentStep || !lab) return;

    const isRecheck = phase === 'feedback';
    const outcome = evaluate(currentStep);

    if (isRecheck && (currentStep.type === 'fill_in_blank' || currentStep.type === 'code_output')) {
      setRecheckValidation(outcome === 'correct' ? 'correct' : 'incorrect');
      return;
    }

    setRecheckValidation(null);
    setResult(outcome);
    setPhase('feedback');

    if (currentStep.type === 'rapid_match') {
      setRapidMatchStates((prev) => {
        const current = prev[currentStep.id];
        if (!current) return prev;
        return {
          ...prev,
          [currentStep.id]: {
            ...current,
            phase: 'feedback',
            isFlashing: false
          }
        };
      });
    }

    // Set validation state for highlighting
    if (currentStep.type === 'code') {
      setValidationState(outcome === 'correct' ? 'valid' : 'invalid');
    }

    if (outcome === 'incorrect' && currentStep.type === 'code' && currentStep.solution) {
      setShowSolution({ ...showSolution, [currentStep.id]: true });
    }

    setLastAwardType(null);
    setLastXpDelta(null);

    // Don't award XP for completed lab retries
    const isRetry = typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("retry") === "1";
    const skipRewards = isRetry && labProgress?.completed;

    // Figure out partial credit ratio when relevant
    let partialCredit: number | undefined;
    if (outcome === 'partial') {
      // DISABLED: task step-type
      // if (currentStep.type === 'task') {
      //   const progressSet = taskProgress[currentStep.id] || new Set<number>();
      //   const totalItems = currentStep.checklist?.length || 0;
      //   partialCredit = totalItems > 0 ? progressSet.size / totalItems : 0.5;
      // } else {
      //   partialCredit = 0.5;
      // }
      partialCredit = 0.5;
    }

    // Calculate XP change locally without recording to backend yet
    if (!skipRewards) {
      const labIdNum = parseInt(lab.id);
      const total = lab.steps?.length || 1;
      const isLastStep = currentStepIndex >= total - 1;

      // Get lab difficulty and corresponding XP values
      const difficultyKey = (lab.difficulty?.toLowerCase() as DifficultyKey) || 'beginner';
      const xpConfig = DIFFICULTY_XP[difficultyKey] || DIFFICULTY_XP.beginner;

      // Determine XP delta based on outcome
      let xpChange = 0;
      let awardType: 'full' | 'partial' | 'loss' = 'full';
      
      // Special handling for typing-test: only award XP on first completion
      if (currentStep?.type === 'typing-test') {
        const alreadyAwarded = typingTestResults[currentStep.id]?.alreadyAwarded || false;
        if (alreadyAwarded) {
          // No XP for subsequent completions
          xpChange = 0;
          awardType = 'full'; // Still show success feedback
        } else {
          // First completion: award full XP
          xpChange = xpConfig.full;
          awardType = 'full';
          // Mark as awarded
          setTypingTestResults({
            ...typingTestResults,
            [currentStep.id]: {
              ...typingTestResults[currentStep.id],
              alreadyAwarded: true
            }
          });
        }
      } else if (outcome === 'correct') {
        xpChange = xpConfig.full;
        awardType = 'full';
      } else if (outcome === 'partial') {
        // Calculate partial XP based on partialCredit ratio
        const partialValue = typeof partialCredit === 'number' 
          ? Math.round(xpConfig.full * partialCredit)
          : Math.round(xpConfig.full * 0.5);
        xpChange = Math.min(
          xpConfig.full - 1,
          Math.max(xpConfig.partialFloor, partialValue || xpConfig.partialFloor)
        );
        awardType = 'partial';
      } else {
        xpChange = -xpConfig.full;
        awardType = 'loss';
      }

      setLastAwardType(awardType);
      setLastXpDelta(xpChange);
      triggerXpAnimation(awardType);
      if (xpChange !== 0) {
        setXp((x) => Math.max(0, x + xpChange));
      }

      // Store this step's result for batch submission
      const stepOutcome = outcome === 'correct' ? 'full' : outcome === 'partial' ? 'partial' : 'incorrect';
      setStepResults(prev => [
        ...prev.filter(s => s.stepIndex !== currentStepIndex),
        { stepIndex: currentStepIndex, outcome: stepOutcome, partialCredit }
      ]);

      // Only record to backend on last step - submit all steps at once
      if (isLastStep) {
        try {
          // Get all step results including current one
          const allResults = [
            ...stepResults.filter(s => s.stepIndex !== currentStepIndex),
            { stepIndex: currentStepIndex, outcome: stepOutcome, partialCredit }
          ];
          
          // Submit each step attempt
          for (const stepResult of allResults) {
            await apiService.recordStepAttempt(
              labIdNum,
              stepResult.stepIndex,
              stepResult.outcome as 'full' | 'partial' | 'incorrect',
              stepResult.partialCredit
            );
          }
        } catch (error) {
          console.error('Failed to record step attempts:', error);
        }
      }
    }

    if (outcome === 'incorrect') {
      // Don't lose hearts for completed lab retries
      if (!(isRetry && labProgress?.completed)) {
        setHearts((h) => Math.max(0, h - 1));
      }
    }
  };


  const handleContinue = async () => {
    if (!lab) return;

    const total = lab.steps?.length || 1;
    const isLastStep = currentStepIndex >= total - 1;
    const stepsCompleted = isLastStep ? total : currentStepIndex + 1;
    const currentStepId = lab.steps?.[currentStepIndex]?.id;

    if (isLastStep) {
      await persistProgress(stepsCompleted, true);
    }

    // Skip completion UI for completed lab retries
    const isRetry = typeof window !== "undefined" && 
      new URLSearchParams(window.location.search).get("retry") === "1";
    if (isRetry && labProgress?.completed) {
      // Just continue to next step or close without showing completion UI
      if (isLastStep) {
        // If it's the last step in retry mode, close the lab
        setTimeout(() => {
          if (window.parent !== window) {
            // Send message to parent to close modal and redirect
            window.parent.postMessage({
              type: 'lab:completed',
              redirectInfo: courseInfo ? {
                courseId: courseInfo.courseId,
                pageIndex: courseInfo.pageIndex + 1
              } : null
            }, '*');
          } else {
            // Fallback for non-embedded mode
            if (courseInfo) {
              const nextPageIndex = courseInfo.pageIndex + 1;
              window.location.href = `/courses/${courseInfo.courseId}?page=${nextPageIndex}`;
            } else {
              window.location.href = '/courses';
            }
          }
        }, 400);
        return;
      }
      // Otherwise, continue to next step normally
      clearStepState(currentStepId);
      setPhase('answer');
      setResult(null);
      setSelectedChoice(null);
      setValidationErrors([]);
      setValidationState(null);
      setRecheckValidation(null);
      setTimeout(() => setCurrentStepIndex((i) => Math.min(i + 1, total - 1)), 100);
      return;
    }

    if (isLastStep) {
      // Only show badge if not in retry mode
      const isRetry = typeof window !== "undefined" && 
        new URLSearchParams(window.location.search).get("retry") === "1";
      
      if (!isRetry) {
        // Show badge for first completion only
        alert(String.fromCharCode(55356, 57286) + ' Lab Completion Badge Earned!');
      }
      
      setTimeout(() => {
        if (window.parent !== window) {
          // Send message to parent to close modal and redirect
          window.parent.postMessage({
            type: 'lab:completed',
            redirectInfo: courseInfo ? {
              courseId: courseInfo.courseId,
              pageIndex: courseInfo.pageIndex + 1
            } : null
          }, '*');
        } else {
          // Fallback for non-embedded mode
          if (courseInfo) {
            const nextPageIndex = courseInfo.pageIndex + 1;
            window.location.href = `/courses/${courseInfo.courseId}?page=${nextPageIndex}`;
          } else {
            window.location.href = '/courses';
          }
        }
      }, 400);
      return;
    }

    clearStepState(currentStepId);
    setPhase('answer');
    setResult(null);
    setSelectedChoice(null);
    setValidationErrors([]);
    setValidationState(null);
    setRecheckValidation(null);
    setTimeout(() => setCurrentStepIndex((i) => Math.min(i + 1, total - 1)), 100);
  };

  
  // @ts-expect-error: Retain handler for re-enabling task steps
  const handleTaskToggle = (stepId: string, taskIndex: number) => {
    const current = taskProgress[stepId] || new Set();
    const updated = new Set(current);
    if (updated.has(taskIndex)) {
      updated.delete(taskIndex);
    } else {
      updated.add(taskIndex);
    }
    setTaskProgress({ ...taskProgress, [stepId]: updated });
  };
  
  const handleTypingRetry = () => {
    if (currentStep?.id) {
      const stepId = currentStep.id;
      setTypingTestSessions(prev => ({
        ...prev,
        [stepId]: (prev[stepId] || 0) + 1,
      }));
      setTypingTestResults(prev => {
        const existing = prev[stepId] || {
          completed: false,
          wpm: 0,
          accuracy: 100,
          mistakes: 0,
          alreadyAwarded: false,
        };
        return {
          ...prev,
          [stepId]: {
            ...existing,
            completed: false,
            wpm: 0,
            accuracy: 100,
            mistakes: 0,
          }
        };
      });
    }

    // Reset phase back to answer mode so user can see the test again
    setPhase('answer');
    setResult(null);
  };

  const handleCodeInput = (stepId: string, value: string) => {
    setCodeInputs({ ...codeInputs, [stepId]: value });
  };

  const validateBlank = (userAnswer: string, blank: { answer: string; alternatives?: string[]; caseSensitive?: boolean }) => {
    const normalize = (str: string) => blank.caseSensitive ? str.trim() : str.trim().toLowerCase();
    const normalized = normalize(userAnswer);
    const correctAnswers = [blank.answer, ...(blank.alternatives || [])];
    return correctAnswers.some(ans => normalize(ans) === normalized);
  };


  const renderStepContent = (step: LabStep | undefined) => {

    if (!step) {
      return (
        <div className="duo-bubble">
          <div className="duo-bubble-text">This lab does not have any steps yet.</div>
        </div>
      );
    }
    switch (step.type) {
      case "read":
        return (
          <div className="duo-bubble">
            <div className="duo-bubble-avatar"><Avatar avatarId={activeAvatarId} size={56} mood="encouraging" /></div>
             <div className="duo-bubble-text" 
               {...(step.content && containsHtml(step.content) ? { dangerouslySetInnerHTML: htmlContent(step.content) } : { children: step.content })}
             />
          </div>
        );

      case "quiz":
        return (
          <>
            {step.question && (
              <div className="duo-bubble" style={{ marginBottom: 16 }}>
                <div className="duo-bubble-avatar"><Avatar avatarId={activeAvatarId} size={56} mood="neutral" /></div>
                <div className="duo-bubble-text" style={{ fontWeight: 600 }}
                  {...(containsHtml(step.question) ? { dangerouslySetInnerHTML: htmlContent(step.question) } : { children: step.question })}
                />
              </div>
            )}
            <div className="duo-choices" style={{ gap: isMobileLayout ? 10 : undefined }}>
              {step.choices?.map((choice, index) => {
              const selected = selectedChoice === index;
              const isAnswer = index === step.answerIndex;
              const showFb = phase === 'feedback';
              const state = showFb
                ? (selected && isAnswer) || (!selected && isAnswer)
                  ? 'ok'
                  : selected
                  ? 'bad'
                  : 'idle'
                : selected
                ? 'active'
                : 'idle';
               return (
                 <div
                   key={index}
                   ref={setChoiceRef(index)}
                   className={`duo-choice ${state}`}
                   onClick={() => phase === 'answer' && setSelectedChoice(index)}
                   {...(containsHtml(choice) ? { dangerouslySetInnerHTML: htmlContent(choice) } : { children: choice })}
                 />
               );
            })}
               {phase === 'feedback' && step.explanation && (
                 <div className="duo-bubble-text" style={{ marginTop: 8 }}>
                   <strong>
                     {result === 'correct'
                       ? 'Correct! '
                       : result === 'partial'
                         ? 'Almost there. '
                         : 'Not quite. '}
                   </strong>
                   <span {...(containsHtml(step.explanation) ? { dangerouslySetInnerHTML: htmlContent(step.explanation) } : { children: step.explanation })} />
                 </div>
               )}
            </div>
          </>
        );

      // DISABLED: task step-type
      // case "task": {
      //   const progress = taskProgress[step.id] || new Set<number>();
      //   return (
      //      <div className="duo-task-list">
      //        {step.checklist?.map((item, idx) => (
      //         <div
      //           key={idx}
      //           className={`duo-task-chip ${progress.has(idx) ? 'on' : ''}`}
      //           onClick={() => handleTaskToggle(step.id, idx)}
      //         >
      //           <span className="dot" />
      //           <span>{item}</span>
      //         </div>
      //       ))}
      //     </div>
      //   );
      // }

      case "code": {
        const codeValue = codeInputs[step.id] || "";
        return (
          <div className="duo-code">
            <div className="duo-code-prompt" dangerouslySetInnerHTML={{__html: step.prompt || ''}} />
            <TUICodeEditor
              value={codeValue}
              onChange={(value) => handleCodeInput(step.id, value)}
              placeholder={step.placeholder}
              language={step.language || "javascript"}
              height={300}
              tests={step.tests}
              validationState={validationState}
              solution={step.solution}
              onTestRun={(isValid) => setValidationState(isValid ? 'valid' : 'invalid')}
            />
            {phase === 'feedback' && result === 'incorrect' && validationErrors.length > 0 && (
              <div style={{ 
                marginTop: '12px', 
                padding: '12px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffc107',
                borderRadius: '6px',
                fontSize: '0.9rem'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '6px', color: '#856404' }}>
                  {validationErrors.length === 1 ? 'Issue found:' : 'Issues found:'}
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#856404' }}>
                  {validationErrors.map((error, idx) => (
                    <li key={idx}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            {step.solution && phase === 'feedback' && result === 'incorrect' && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 600, marginBottom: 6, color: '#1a73e8' }}>Teacher hint:</div>
                <div className="duo-bubble-text" style={{ 
                  padding: '12px', 
                  backgroundColor: '#e8f4fd', 
                  border: '1px solid #1a73e8',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  fontSize: '0.9rem'
                }}>{step.solution}</div>
              </div>
            )}
          </div>
        );
      }

      case "multi_select": {
        const selected = multiSelectAnswers[step.id] || new Set<number>();
        return (
           <>
             {step.question && (
               <div className="duo-bubble" style={{ marginBottom: 16 }}>
                 <div className="duo-bubble-avatar"><Avatar avatarId={activeAvatarId} size={56} mood="neutral" /></div>
                 <div className="duo-bubble-text" style={{ fontWeight: 600 }}
                   {...(containsHtml(step.question) ? { dangerouslySetInnerHTML: htmlContent(step.question) } : { children: step.question })}
                 />
               </div>
             )}
            <div className="duo-choices" style={{ gap: isMobileLayout ? 10 : undefined }}>
              {step.choices?.map((choice, index) => {
              const isSelected = selected.has(index);
              const isCorrect = step.correctIndices?.includes(index);
              const showFb = phase === 'feedback';
              const state = showFb
                ? isSelected && isCorrect 
                  ? 'ok' 
                  : isSelected && !isCorrect
                  ? 'bad'
                  : !isSelected && isCorrect
                  ? 'ok-missed'
                  : 'idle'
                : isSelected
                ? 'active'
                : 'idle';
                
              return (
                <label
                  key={index}
                  className={`duo-choice ${state}`}
                  style={{ cursor: phase === 'answer' ? 'pointer' : 'default', display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                      if (phase === 'answer') {
                        const newSet = new Set(selected);
                        if (isSelected) newSet.delete(index);
                        else newSet.add(index);
                        setMultiSelectAnswers({ ...multiSelectAnswers, [step.id]: newSet });
                      }
                    }}
                    disabled={phase === 'feedback'}
                    style={{ marginRight: '12px', width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                   <span {...(containsHtml(choice) ? { dangerouslySetInnerHTML: htmlContent(choice) } : { children: choice })} />
                </label>
              );
            })}
              {phase === 'feedback' && step.explanation && (
                <div className="duo-bubble-text" style={{ marginTop: 8 }}>
                   <strong>{result === 'correct' ? 'Perfect! ' : result === 'partial' ? 'Good try! ' : 'Not quite. '}</strong>
                   <span {...(containsHtml(step.explanation) ? { dangerouslySetInnerHTML: htmlContent(step.explanation) } : { children: step.explanation })} />
                </div>
              )}
            </div>
          </>
        );
      }

      case "fill_in_blank": {
        const answers = fillInBlankAnswers[step.id] || [];
        
        return (
          <div className="duo-fill-blank">
            {step.question && (
               <div className="duo-bubble" style={{ marginBottom: 16 }}>
                 <div className="duo-bubble-avatar"><Avatar avatarId={activeAvatarId} size={56} mood="neutral" /></div>
                 <div className="duo-bubble-text" style={{ fontWeight: 600 }}
                   {...(containsHtml(step.question) ? { dangerouslySetInnerHTML: htmlContent(step.question) } : { children: step.question })}
                 />
               </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <CodeDisplay code={step.template || ''} language={step.language || 'python'} />
            </div>
            <div style={{ marginTop: 12 }}>
              {step.blanks?.map((blank, idx) => {
                const isCorrect = phase === 'feedback' ? validateBlank(answers[idx] || '', blank) : null;
                return (
                  <div key={idx} style={{ marginBottom: 8 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: '0.9rem', fontWeight: 500 }}>
                      Blank #{idx + 1}:
                    </label>
                    <input
                      type="text"
                      value={answers[idx] || ''}
                      onChange={(e) => {
                        const newAnswers = [...answers];
                        newAnswers[idx] = e.target.value;
                        setFillInBlankAnswers({ ...fillInBlankAnswers, [step.id]: newAnswers });
                      }}
                      placeholder="Enter your answer..."
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '14px',
                        border: phase === 'feedback' 
                          ? (isCorrect ? '2px solid #4caf50' : '2px solid #f44336')
                          : '1px solid #ccc',
                        borderRadius: '4px',
                        backgroundColor: phase === 'feedback'
                          ? (isCorrect ? '#e8f5e9' : '#ffebee')
                          : '#fff'
                      }}
                    />
                  </div>
                );
              })}
            </div>
            {phase === 'feedback' && step.explanation && (
              <div className="duo-bubble-text" style={{ marginTop: 12 }}>
                <strong>
                  {recheckValidation !== null
                    ? recheckValidation === 'correct' 
                      ? 'Correct on the Retry! '
                      : 'Incorrect on the Retry! '
                    : result === 'correct' 
                      ? 'Correct! ' 
                      : result === 'partial' 
                        ? 'Close! ' 
                        : 'Try again. '}
                </strong>
                <span {...(containsHtml(step.explanation) ? { dangerouslySetInnerHTML: htmlContent(step.explanation) } : { children: step.explanation })} />
              </div>
            )}
          </div>
        );
      }

      case "code_output": {
        const userOutput = codeOutputAnswers[step.id] || '';
        return (
          <div className="duo-code-output">
            {(step.title || step.question) && (
              <div className="duo-bubble" style={{ marginBottom: 16 }}>
                <div className="duo-bubble-avatar"><Avatar avatarId={activeAvatarId} size={56} mood="neutral" /></div>
                <div className="duo-bubble-text" style={{ fontWeight: 600 }}>
                  {step.title || step.question || "What will this code output?"}
                </div>
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <CodeDisplay code={step.code || ''} language={step.language || 'javascript'} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Your answer:</label>
              <textarea
                value={userOutput}
                onChange={(e) => setCodeOutputAnswers({ ...codeOutputAnswers, [step.id]: e.target.value })}
                placeholder="Enter the expected output..."
                rows={3}
                spellCheck={false}
                autoCorrect="off"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  border: phase === 'feedback'
                    ? (result === 'correct' ? '2px solid #4caf50' : '2px solid #f44336')
                    : '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: phase === 'feedback' 
                    ? (result === 'correct' ? '#e8f5e9' : '#ffebee')
                    : '#fff',
                  resize: 'vertical',
                  minHeight: '96px'
                }}
              />
            </div>
            {phase === 'feedback' && step.explanation && (
              <div className="duo-bubble-text" style={{ marginTop: 12 }}>
                <strong>
                  {recheckValidation !== null
                    ? recheckValidation === 'correct' 
                      ? 'Correct on the Retry! '
                      : 'Incorrect on the Retry! '
                    : result === 'correct' 
                      ? 'Correct! ' 
                      : 'Not quite. '}
                </strong>
                <span {...(containsHtml(step.explanation) ? { dangerouslySetInnerHTML: htmlContent(step.explanation) } : { children: step.explanation })} />
                {result === 'incorrect' && step.correctOutput && (
                  <div style={{ marginTop: 8, padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', fontFamily: 'monospace' }}>
                    Expected: <code>{step.correctOutput}</code>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }

      case "typing-test": {
        const handleTypingComplete = (results: TypingTestResults) => {
          // Store results
          const alreadyAwarded = typingTestResults[step.id]?.alreadyAwarded || false;
          setTypingTestResults({
            ...typingTestResults,
            [step.id]: {
              completed: true,
              wpm: results.wpm,
              accuracy: results.accuracy,
              mistakes: results.mistakes,
              alreadyAwarded
            }
          });
          
          // Auto-advance to feedback phase
          setTimeout(() => {
            handleCheck();
          }, 100);
        };

        const handleTypingUpdate = (results: TypingTestResults) => {
          // Update metrics in real-time
          const alreadyAwarded = typingTestResults[step.id]?.alreadyAwarded || false;
          setTypingTestResults({
            ...typingTestResults,
            [step.id]: {
              completed: results.completed,
              wpm: results.wpm,
              accuracy: results.accuracy,
              mistakes: results.mistakes,
              alreadyAwarded
            }
          });
        };

        return (
          <div className="typing-test-wrapper">
            {step.title && (
              <div className="duo-bubble" style={{ marginBottom: 16 }}>
                <div className="duo-bubble-avatar"><Avatar avatarId={activeAvatarId} size={56} mood="neutral" /></div>
                <div className="duo-bubble-text" style={{ fontWeight: 600 }}>
                  {step.title}
                </div>
              </div>
            )}
            <TypingTest
              key={`${step.id}-${typingTestSessions[step.id] || 0}`}
              targetText={step.targetText || "The quick brown fox jumps over the lazy dog."}
              testDuration={step.testDuration || 30}
              showKeyboard={step.showKeyboard !== false}
              onComplete={handleTypingComplete}
              onUpdate={handleTypingUpdate}
              onRetry={handleTypingRetry}
            />
            {phase === 'feedback' && step.explanation && (
               <div className="duo-bubble-text" style={{ marginTop: 16 }}>
                 <strong>Great job! </strong>
                 <span {...(containsHtml(step.explanation) ? { dangerouslySetInnerHTML: htmlContent(step.explanation) } : { children: step.explanation })} />
               </div>
            )}
          </div>
        );
      }

      case "ascii_connect_dots": {
        const userSequence = asciiConnectDotsAnswers[step.id] || [];
        
        return (
          <>
            {step.hint && phase === 'answer' && (
              <div className="duo-bubble" style={{ marginBottom: 16 }}>
                <div className="duo-bubble-avatar"><Avatar avatarId={activeAvatarId} size={56} mood="neutral" /></div>
                <div className="duo-bubble-text">
                  {step.hint}
                </div>
              </div>
            )}
            
            <AsciiConnectDots
              decorativeTitle={step.decorativeTitle}
              asciiArt={step.asciiArt || ''}
              nodes={step.nodes || []}
              onSequenceChange={(sequence) => {
                setAsciiConnectDotsAnswers({
                  ...asciiConnectDotsAnswers,
                  [step.id]: sequence
                });
              }}
              userSequence={userSequence}
              showFeedback={phase === 'feedback'}
              correctSequence={step.correctSequence}
            />
            
            {phase === 'feedback' && step.explanation && (
              <div className="duo-bubble-text" style={{ marginTop: 16 }}>
                <strong>
                  {result === 'correct'
                    ? 'Perfect! '
                    : result === 'partial'
                      ? 'Good start! '
                      : 'Not quite. '}
                </strong>
                <span {...(containsHtml(step.explanation) ? { dangerouslySetInnerHTML: htmlContent(step.explanation) } : { children: step.explanation })} />
              </div>
            )}
          </>
        );
      }

      case "rapid_match": {
        const matchState = rapidMatchStates[step.id];
        const selectedKey = matchState?.selectedKey ?? null;
        const selectedValue = matchState?.selectedValue ?? null;
        const keyOrder = matchState?.keyOrder?.length ? matchState.keyOrder : (step.keys || []);
        const valueOrder = matchState?.valueOrder?.length ? matchState.valueOrder : (step.values || []);
        const isFlashPhase = matchState?.phase === 'flash';
        const isAnswerPhase = phase === 'answer' && matchState?.phase === 'answer';

        const handleDragStart = (itemType: 'key' | 'value', itemValue: string) => (event: DragEvent<HTMLDivElement>) => {
          if (!isAnswerPhase) return;
          event.dataTransfer.setData('text/plain', JSON.stringify({
            stepId: step.id,
            type: itemType,
            value: itemValue
          }));
          event.dataTransfer.effectAllowed = 'move';
        };

        const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
          if (!isAnswerPhase) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
        };

        const handleDrop = (itemType: 'key' | 'value') => (event: DragEvent<HTMLDivElement>) => {
          if (!isAnswerPhase) return;
          event.preventDefault();
          const rawPayload = event.dataTransfer.getData('text/plain');
          if (!rawPayload) return;
          try {
            const payload = JSON.parse(rawPayload) as { stepId?: string; type?: string; value?: string };
            if (payload.stepId !== step.id || payload.type !== itemType || typeof payload.value !== 'string') {
              return;
            }
            const nextValue = payload.value ?? null;

            setRapidMatchStates((prev) => {
              const current = prev[step.id];
              if (!current) return prev;
              return {
                ...prev,
                [step.id]: {
                  ...current,
                  selectedKey: itemType === 'key' ? nextValue : (current.selectedKey ?? null),
                  selectedValue: itemType === 'value' ? nextValue : (current.selectedValue ?? null)
                }
              };
            });
          } catch (error) {
            return;
          }
        };

        const renderFlashCard = () => {
          if (!isFlashPhase) return null;
          if (step.flashCardFormat === 'code') {
            return (
              <div className="rapid-match-flash">
                <CodeDisplay code={step.flashCard || ''} language={step.flashCardLanguage || 'javascript'} />
              </div>
            );
          }
          if (step.flashCardFormat === 'html') {
            return (
              <div className="rapid-match-flash" dangerouslySetInnerHTML={{ __html: step.flashCard || '' }} />
            );
          }
          return (
            <div className="rapid-match-flash">
              {step.flashCard}
            </div>
          );
        };

        return (
          <div className="rapid-match">
            <div className="rapid-match-prompt"
              {...(step.prompt && containsHtml(step.prompt)
                ? { dangerouslySetInnerHTML: htmlContent(step.prompt) }
                : { children: step.prompt })}
            />

            {renderFlashCard()}

            <div className="rapid-match-play">
              {isFlashPhase && (
                <div className="rapid-match-countdown" aria-hidden="true">
                  <span className="rapid-match-countdown-step rapid-match-countdown-one">1</span>
                  <span className="rapid-match-countdown-step rapid-match-countdown-two">2</span>
                  <span className="rapid-match-countdown-step rapid-match-countdown-three">Go!</span>
                </div>
              )}

              <div className="rapid-match-slots">
                <div className={`rapid-match-slot ${selectedKey ? 'filled' : ''}`} onDragOver={handleDragOver} onDrop={handleDrop('key')}>
                  {selectedKey || 'Drag a key'}
                </div>
                <div className={`rapid-match-slot ${selectedValue ? 'filled' : ''}`} onDragOver={handleDragOver} onDrop={handleDrop('value')}>
                  {selectedValue || 'Drag a value'}
                </div>
              </div>

              <div className="rapid-match-options">
                <div className="rapid-match-column">
                  <div className="rapid-match-column-title">Keys</div>
                  <div className="rapid-match-list">
                    {keyOrder.map((keyItem) => {
                      const isSelected = keyItem === selectedKey;
                      const shouldHide = phase !== 'feedback' && isSelected;
                      const shouldRemove = phase === 'feedback' && keyItem !== selectedKey;
                      return (
                        <div
                          key={keyItem}
                          className={`rapid-match-option${shouldHide ? ' hidden' : ''}${shouldRemove ? ' removed' : ''}`}
                          draggable={isAnswerPhase && !shouldHide && !shouldRemove}
                          onDragStart={handleDragStart('key', keyItem)}
                        >
                          {keyItem}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="rapid-match-column">
                  <div className="rapid-match-column-title">Values</div>
                  <div className="rapid-match-list">
                    {valueOrder.map((valueItem) => {
                      const isSelected = valueItem === selectedValue;
                      const shouldHide = phase !== 'feedback' && isSelected;
                      const shouldRemove = phase === 'feedback' && valueItem !== selectedValue;
                      return (
                        <div
                          key={valueItem}
                          className={`rapid-match-option${shouldHide ? ' hidden' : ''}${shouldRemove ? ' removed' : ''}`}
                          draggable={isAnswerPhase && !shouldHide && !shouldRemove}
                          onDragStart={handleDragStart('value', valueItem)}
                        >
                          {valueItem}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {phase === 'feedback' && step.explanation && (
              <div className="duo-bubble-text" style={{ marginTop: 16 }}>
                <strong>
                  {result === 'correct'
                    ? 'Correct! '
                    : result === 'partial'
                      ? 'Close! '
                      : 'Not quite. '}
                </strong>
                <span {...(containsHtml(step.explanation) ? { dangerouslySetInnerHTML: htmlContent(step.explanation) } : { children: step.explanation })} />
              </div>
            )}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="duo-root">
      {/* HUD */}
      <div className="duo-hud">
        <div className="duo-hud-left">
          <div className="duo-lives">
            {Array.from({ length: 3 }).map((_, i) => (
              <span key={i} className={`heart ${i < hearts ? 'on' : ''}`}>❤</span>
            ))}
          </div>
          <div className="duo-xp" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span>XP {xp}</span>
            {xpAnimationType && (
              <span style={{ width: 32, height: 32, display: 'inline-flex' }}>
                <LabAnimation type={xpAnimationType} size={32} />
              </span>
            )}
          </div>
        </div>
        <div className="duo-hud-center">
          <div className="duo-progress">
            <div className="duo-progress-bar"><div className="duo-progress-fill" style={{ width: `${progressPercent}%` }} /></div>
            <div className="duo-progress-meta">
              {displayedStepIndex} / {lab.steps?.length || 0}
              {savingProgress ? ' · Saving…' : ''}
            </div>
          </div>
        </div>
      </div>

 
       {/* Floating mascot that shifts position based on phase */}

      <div className={`duo-mascot ${phase === 'answer' ? 'pos-ask' : result === 'correct' ? 'pos-happy' : result === 'partial' ? 'pos-partial' : 'pos-loss'}`}
           style={mascotPos ? { position: 'absolute', top: mascotPos.top, left: mascotPos.left } : undefined}>
        <Avatar avatarId={activeAvatarId} size={120} mood={avatarMood} />
        <div className="duo-speech">
          {phase === 'answer' && (currentStep?.type === 'code' ? 'Craft the perfect syntax and logic!' : 'Pick the best answer!')}
          {phase === 'feedback' && feedbackMessage}
        </div>
      </div>

      {/* Stage */}
      <div
        className={`duo-stage ${phase === 'feedback' && result === 'incorrect' ? 'shake' : ''}`}
        style={{ padding: isMobileLayout ? "16px 12px" : undefined, marginTop: isMobileLayout ? 12 : undefined }}
      >
        <div
          className="duo-card"
          style={{ padding: isMobileLayout ? "16px" : undefined, marginBottom: isMobileLayout ? 16 : undefined }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <div className="duo-card-title">{lab.title}</div>
            {currentStep?.type === 'typing-test' && typingTestResults[currentStep.id] && (
              <button className="typing-retry-btn" onClick={handleTypingRetry}>
                ↻ Retry
              </button>
            )}
          </div>
          <div className="duo-card-meta">{courseTitle} • {lab.level}</div>
          <div className="duo-card-body">
            {renderStepContent(currentStep)}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="duo-cta" style={{ padding: isMobileLayout ? "16px 20px" : undefined }}>
        {phase === 'answer' ? (
          <button className={`duo-btn primary ${!currentStep || !canCheck(currentStep) ? 'disabled' : ''}`} disabled={!currentStep || !canCheck(currentStep)} onClick={handleCheck}>
            {currentStep && canCheck(currentStep) && <span className="icon"><LabAnimation type="loading" size={18} /></span>}
            Check
          </button>
        ) : (
          <>
            {/* Show Check button during feedback for fill_in_blank and code_output types */}
            {currentStep && (currentStep.type === 'fill_in_blank' || currentStep.type === 'code_output') && (
              <button 
                className={`duo-btn primary ${!canCheck(currentStep) ? 'disabled' : ''}`} 
                disabled={!canCheck(currentStep)} 
                onClick={handleCheck}
                style={{ marginBottom: '12px' }}
              >
                {canCheck(currentStep) && <span className="icon"><LabAnimation type="loading" size={18} /></span>}
                Check
              </button>
            )}
            <button
              ref={ctaRef}
              className={`duo-btn ${result === 'correct' ? 'success' : 'warn'}`}
              onClick={handleContinue}
              disabled={savingProgress}
            >
              {!savingProgress && lastAwardType && (
                <span className="icon">
                  <LabAnimation
                    type={lastAwardType === 'full' ? 'success' : lastAwardType === 'partial' ? 'partial' : 'loss'}
                    size={22}
                  />
                </span>
              )}
              {savingProgress ? 'Saving…' : 'Continue →'}
            </button>
          </>
        )}
        {saveError && (
          <div style={{ marginTop: 12, color: '#f87171', fontSize: '0.85rem' }}>{saveError}</div>
        )}
      </div>
    </div>
  );

}
